/* eslint-disable camelcase */
import { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { NotificationContext } from '../notification-bar/NotificationBar';
import { NOTIFICATION_TYPES } from '@mdc/constants';
import { Authenticator, BackendService, logService } from '@mdc/services';
import PropTypes from 'prop-types';

const UserContext = createContext(Authenticator.getUser());
const STATES = {
    PENDING: 'pending',
    LOGGED_IN: 'logged in',
    LOGGED_OUT: 'logged out',
    IP_RESTRICTION: 'ip restriction'
};
export const UserProvider = ({ children, value }) => {
    const [user, setUser] = useState(null);
    const [isClient, setIsClient] = useState(false);
    const [isOrganizationAdmin, setIsOrganizationAdmin] = useState();
    const [isOrganizationEnterprise, setIsOrganizationEnterprise] = useState();
    const { notify } = useContext(NotificationContext);

    const getUserState = () => {
        if (!isClient) {
            return STATES.PENDING;
        }
        if (typeof user === 'undefined') {
            return STATES.LOGGED_OUT;
        }

        if (typeof user === 'object' && user !== null) {
            if (user.ipRestriction) {
                return STATES.IP_RESTRICTION;
            }
            return STATES.LOGGED_IN;
        }

        return STATES.PENDING;
    };

    const stateObj = useMemo(() => {
        const state = getUserState();
        return {
            data: user,
            state,
            STATES,
            isPaidUser: state === STATES.LOGGED_IN && user?.paid_user === 1,
            isOrganizationAdmin,
            isOrganizationEnterprise
        };
    }, [user, isClient, isOrganizationAdmin, isOrganizationEnterprise]);

    const onStateChange = (userData) => {
        setUser(userData);
    };

    const onError = ({ message }) => {
        notify({
            message: message,
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    useEffect(() => {
        setIsClient(true);

        Authenticator.events.on(Authenticator.events.topics.STATE_CHANGE, onStateChange);
        Authenticator.events.on(Authenticator.events.topics.ERROR, onError);
        return () => {
            Authenticator.events.off(Authenticator.events.topics.STATE_CHANGE, onStateChange);
            Authenticator.events.off(Authenticator.events.topics.ERROR, onError);
        };
    }, []);

    useEffect(() => {
        const state = getUserState();

        if (user?.sso_user_id && user?.organization?.organization_id && state === STATES.LOGGED_IN) {
            (async () => {
                try {
                    // Check if user can access organization items
                    const response = await BackendService.postIsOrganizationAdmin({
                        userId: user.sso_user_id,
                        organizationId: user.organization.organization_id,
                    })[0];

                    if (response.data) {
                        setIsOrganizationAdmin(response.data.isAdmin || false);
                        setIsOrganizationEnterprise(response.data.isEnterprise || false);
                    }
                } catch (e) {
                    logService.error(e);
                }
            })();
        } else {
            setIsOrganizationAdmin(false);
            setIsOrganizationEnterprise(false);
        }

    }, [user]);

    return (
        <UserContext.Provider value={value || stateObj}>
            {children}
        </UserContext.Provider>
    );
};

UserProvider.propTypes = {
    children: PropTypes.element.isRequired,
    value: PropTypes.object
};

export default UserContext;
